import { useAuth } from '../contexts/AuthContext';

/**
 * Componente para renderizar conteúdo baseado em permissões
 * Facilita o controle de acesso em qualquer parte da aplicação
 * 
 * @param {string} permission - Permissão específica requerida
 * @param {string[]} anyOf - Array de permissões (usuário precisa de pelo menos uma)
 * @param {string[]} allOf - Array de permissões (usuário precisa de todas)
 * @param {string} resource - Recurso (ex: 'adolescentes')  
 * @param {string} action - Ação (ex: 'view', 'create', 'edit', 'delete')
 * @param {React.ReactNode} children - Conteúdo a ser renderizado se tiver permissão
 * @param {React.ReactNode} fallback - Conteúdo alternativo se não tiver permissão
 */
export default function PermissionGuard({ 
  permission, 
  anyOf, 
  allOf, 
  resource, 
  action = 'view', 
  children, 
  fallback = null 
}) {
  const { hasPermission, hasAnyPermission, canAccess } = useAuth();

  // Se especificou resource e action, constrói a permissão
  if (resource && action) {
    permission = `${resource}.${action}`;
  }

  let hasAccess = false;

  if (permission) {
    // Verifica permissão específica
    hasAccess = hasPermission(permission);
  } else if (resource && action) {
    // Usa o método canAccess
    hasAccess = canAccess(resource, action);
  } else if (anyOf) {
    // Verifica se tem pelo menos uma das permissões
    hasAccess = hasAnyPermission(anyOf);
  } else if (allOf) {
    // Verifica se tem todas as permissões
    hasAccess = allOf.every(perm => hasPermission(perm));
  }

  return hasAccess ? children : fallback;
}

/**
 * Hook personalizado para verificações de permissão em componentes funcionais
 */
export function usePermissions() {
  const { hasPermission, hasAnyPermission, canAccess, isAdmin } = useAuth();

  return {
    hasPermission,
    hasAnyPermission,
    canAccess,
    isAdmin,
    
    // Métodos de conveniência para recursos específicos
    canViewAdolescentes: () => canAccess('adolescentes', 'view'),
    canCreateAdolescentes: () => canAccess('adolescentes', 'create'),
    canEditAdolescentes: () => canAccess('adolescentes', 'edit'),
    canDeleteAdolescentes: () => canAccess('adolescentes', 'delete'),
    
    canViewMovimentacoes: () => canAccess('movimentacoes', 'view'),
    canCreateMovimentacoes: () => canAccess('movimentacoes', 'create'),
    canEditMovimentacoes: () => canAccess('movimentacoes', 'edit'),
    canDeleteMovimentacoes: () => canAccess('movimentacoes', 'delete'),
    
    canManageUsers: () => canAccess('usuarios', 'view'),
    canManageConfig: () => hasAnyPermission([
      'config.situacoes', 'config.unidades', 'config.modulos', 
      'config.decisoes', 'config.orgaos', 'config.perfis'
    ]),
    
    canViewReports: () => canAccess('relatorios', 'view'),
    canExportReports: () => canAccess('relatorios', 'export'),
  };
}